// ─────────────────────────────────────────
// UserPassport.js — view another golfer's passport
// ─────────────────────────────────────────
// Mirrors the own Passport page structure exactly.
// Accessed via /passport/:userId.
// Differences from own Passport: follow/unfollow button, no privacy toggle, no round editing.

import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from '../components/AvatarImg'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

export default function UserPassport({ session }) {
  const isMobile = useIsMobile()
  const { userId } = useParams()
  const navigate = useNavigate()

  const [profile, setProfile] = useState(null)
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)
  const [isFollowing, setIsFollowing] = useState(false)
  const [followerCount, setFollowerCount] = useState(0)
  const [followingCount, setFollowingCount] = useState(0)
  const [followLoading, setFollowLoading] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState(null)
  const [showAllRounds, setShowAllRounds] = useState(false)
  const [followModal, setFollowModal] = useState(null) // 'followers' | 'following'
  const [followModalUsers, setFollowModalUsers] = useState([])
  const [followModalLoading, setFollowModalLoading] = useState(false)

  const isOwnProfile = userId === session.user.id

  useEffect(() => {
    if (isOwnProfile) { navigate('/passport', { replace: true }); return }
    fetchAll()
  }, [userId])

  async function fetchAll() {
    const [{ data: profileData }, { data: followRow }, { data: followersData }, { data: followingData }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).single(),
      supabase.from('follows').select('id').eq('follower_id', session.user.id).eq('following_id', userId).maybeSingle(),
      supabase.from('follows').select('id').eq('following_id', userId),
      supabase.from('follows').select('id').eq('follower_id', userId),
    ])

    setProfile(profileData)
    setIsFollowing(!!followRow)
    setFollowerCount((followersData || []).length)
    setFollowingCount((followingData || []).length)

    const isPrivate = profileData?.visibility === 'private'
    const canSee = !isPrivate || !!followRow

    if (canSee) {
      const { data: roundsData } = await supabase
        .from('rounds')
        .select('*')
        .eq('user_id', userId)
        .neq('data_source', 'Post')
        .order('created_at', { ascending: false })
      setRounds(roundsData || [])
    }

    setLoading(false)
  }

  async function handleFollow() {
    if (followLoading) return
    setFollowLoading(true)
    if (isFollowing) {
      setIsFollowing(false)
      setFollowerCount(c => c - 1)
      await supabase.from('follows').delete()
        .eq('follower_id', session.user.id).eq('following_id', userId)
    } else {
      setIsFollowing(true)
      setFollowerCount(c => c + 1)
      await supabase.from('follows').insert({ follower_id: session.user.id, following_id: userId })
    }
    setFollowLoading(false)
    if (!isFollowing && profile?.visibility === 'private') fetchAll()
  }

  async function openFollowModal(type) {
    setFollowModal(type)
    setFollowModalLoading(true)
    setFollowModalUsers([])
    let data
    if (type === 'followers') {
      const { data: rows } = await supabase
        .from('follows')
        .select('profiles!follows_follower_id_fkey(id, username, full_name, avatar_emoji, avatar_url)')
        .eq('following_id', userId)
      data = (rows || []).map(r => r.profiles).filter(Boolean)
    } else {
      const { data: rows } = await supabase
        .from('follows')
        .select('profiles!follows_following_id_fkey(id, username, full_name, avatar_emoji, avatar_url)')
        .eq('follower_id', userId)
      data = (rows || []).map(r => r.profiles).filter(Boolean)
    }
    setFollowModalUsers(data)
    setFollowModalLoading(false)
  }

  // ── Computed stats ──────────────────────────────────────────
  const totalRounds = rounds.length
  const scoredRounds = rounds.filter(r => r.score != null)
  const bestScore = scoredRounds.length > 0 ? Math.min(...scoredRounds.map(r => r.score)) : null

  const courseMap = {}
  rounds.forEach(r => {
    if (!r.course_name) return
    if (!courseMap[r.course_name]) courseMap[r.course_name] = []
    courseMap[r.course_name].push(r)
  })
  const courses = Object.entries(courseMap)
    .map(([name, courseRounds]) => {
      const scoredHere = courseRounds.filter(r => r.score != null)
      return {
        name,
        timesPlayed: courseRounds.length,
        bestScore: scoredHere.length > 0 ? Math.min(...scoredHere.map(r => r.score)) : null,
        lastPlayed: courseRounds[0].created_at,
        par: courseRounds[0].par
      }
    })
    .sort((a, b) => new Date(b.lastPlayed) - new Date(a.lastPlayed))
  const totalCourses = courses.length

  const thisYear = new Date().getFullYear()
  const roundsThisYear = rounds.filter(r => new Date(r.created_at).getFullYear() === thisYear)
  const scoredThisYear = roundsThisYear.filter(r => r.score != null)
  const avgThisYear = scoredThisYear.length > 0
    ? (scoredThisYear.reduce((sum, r) => sum + r.score, 0) / scoredThisYear.length).toFixed(1)
    : null
  const coursesThisYear = [...new Set(roundsThisYear.filter(r => r.course_name).map(r => r.course_name))].length

  const achievements = []
  if (totalRounds >= 1)  achievements.push({ icon: '⛳', name: 'First Swing', desc: 'Logged first round' })
  if (totalRounds >= 10) achievements.push({ icon: '🔟', name: '10 Rounds Club', desc: '10 rounds logged' })
  if (totalRounds >= 25) achievements.push({ icon: '🎯', name: 'Regular Breakie', desc: '25 rounds logged' })
  if (totalRounds >= 50) achievements.push({ icon: '💼', name: 'Dedicated Golfer', desc: '50 rounds logged' })
  if (totalRounds >= 100) achievements.push({ icon: '🔱', name: 'Century Club', desc: '100 rounds logged' })
  if (totalCourses >= 5)  achievements.push({ icon: '🗺️', name: 'Course Explorer', desc: '5 different courses' })
  if (totalCourses >= 10) achievements.push({ icon: '✈️', name: 'World Traveler', desc: '10 courses played' })
  if (totalCourses >= 25) achievements.push({ icon: '🌍', name: 'Global Golfer', desc: '25 courses played' })
  if (bestScore != null && bestScore <= 99) achievements.push({ icon: '💯', name: 'Broke 100', desc: 'Shot under 100' })
  if (bestScore != null && bestScore <= 89) achievements.push({ icon: '🟢', name: 'Broke 90', desc: 'Shot under 90' })
  if (bestScore != null && bestScore <= 79) achievements.push({ icon: '💎', name: 'Sub-80 Club', desc: 'Shot under 80' })
  if (bestScore != null && bestScore <= 69) achievements.push({ icon: '🚀', name: 'Scratch Territory', desc: 'Shot under 70' })

  const photoRounds = rounds.filter(r => r.photo_url)
  const recentRounds = rounds.slice(0, 5)
  const olderRounds = rounds.slice(5)

  const isPrivate = profile?.visibility === 'private'
  const canSee = !isPrivate || isFollowing

  function memberSinceStr() {
    if (!profile?.created_at) return ''
    return new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }
  function formatDate(d) {
    return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  }
  function formatDateShort(d) {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }
  function scoreBadgeColor(score, par) {
    if (score == null) return '#6b7c6e'
    if (!par) return '#1a7a3c'
    const d = score - par
    if (d < 0) return '#1a7a3c'
    if (d === 0) return '#2980b9'
    return '#c0392b'
  }
  function scoreDiffLabel(score, par) {
    if (score == null) return '—'
    if (!par) return `${score}`
    const d = score - par
    if (d === 0) return `${score} (E)`
    return `${score} (${d > 0 ? '+' : ''}${d})`
  }

  // ── RENDER ──────────────────────────────────────────────────

  if (loading) return (
    <div style={st.loadingScreen}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>⛳</div>
      <div style={{ color: '#6aad80', fontSize: 14, letterSpacing: 0.5 }}>Loading passport...</div>
    </div>
  )

  if (!profile) return (
    <div style={st.loadingScreen}>
      <div style={{ fontSize: 40 }}>❓</div>
      <div style={{ color: 'white', fontSize: 16, marginTop: 12 }}>Player not found</div>
      <button style={st.backBtn} onClick={() => navigate(-1)}>← Go back</button>
    </div>
  )

  return (
    <div style={{ ...st.page, paddingBottom: isMobile ? 72 : 0 }}>

      {/* ── LIGHTBOX ── */}
      {lightboxUrl && (
        <div style={st.lightboxOverlay} onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="" style={st.lightboxImg} />
          <button style={st.lightboxClose}>✕</button>
        </div>
      )}

      {/* ── FOLLOW MODAL ── */}
      {followModal && (
        <div style={st.modalOverlay} onClick={() => setFollowModal(null)}>
          <div style={st.modalSheet} onClick={e => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <div style={st.modalTitle}>{followModal === 'followers' ? 'Followers' : 'Following'}</div>
              <button style={st.modalClose} onClick={() => setFollowModal(null)}>✕</button>
            </div>
            {followModalLoading && <div style={st.modalEmpty}>Loading...</div>}
            {!followModalLoading && followModalUsers.length === 0 && (
              <div style={st.modalEmpty}>No {followModal} yet.</div>
            )}
            {followModalUsers.map(u => (
              <div key={u.id} style={st.modalRow}>
                <div style={st.modalAvatar}><AvatarImg profile={u} /></div>
                <div style={st.modalUserInfo}>
                  <div style={st.modalUserName}>{u.full_name || u.username || 'Golfer'}</div>
                  {u.username && <div style={st.modalUserHandle}>@{u.username}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TOP BAR ── */}
      <div style={st.topbar}>
        <button style={st.backBtnTop} onClick={() => navigate(-1)}>← Back</button>
        <div style={st.logo}>BREAKIE</div>
        <div style={{ width: 60 }} />
      </div>

      <Nav />

      {/* ── HERO ── */}
      <div style={st.hero}>
        <div style={st.heroContent}>
          <div style={st.heroAvatar}><AvatarImg profile={profile} /></div>
          <div style={st.heroInfo}>
            <div style={st.heroName}>{profile?.full_name || profile?.username || 'Golfer'}</div>
            {profile?.username && <div style={st.heroHandle}>@{profile.username}</div>}
            {profile?.bio && <div style={st.heroBio}>{profile.bio}</div>}

            <div style={st.heroFollowRow}>
              <button style={st.heroFollowBtn} onClick={() => openFollowModal('followers')}>
                <span style={st.heroFollowNum}>{followerCount}</span>
                <span style={st.heroFollowLbl}>Followers</span>
              </button>
              <span style={st.heroFollowDot}>·</span>
              <button style={st.heroFollowBtn} onClick={() => openFollowModal('following')}>
                <span style={st.heroFollowNum}>{followingCount}</span>
                <span style={st.heroFollowLbl}>Following</span>
              </button>
            </div>

            <div style={st.heroMeta}>
              {profile?.location && <span style={st.heroMetaItem}>📍 {profile.location}</span>}
              <span style={st.heroMetaItem}>Since {memberSinceStr()}</span>
              {profile?.handicap != null && <span style={st.hcpBadge}>HCP {profile.handicap}</span>}
            </div>
          </div>
        </div>

        {/* Follow / Unfollow + Message */}
        <div style={st.followRow}>
          <button
            style={{ ...st.followBtn, ...(isFollowing ? st.followingBtn : st.notFollowingBtn), opacity: followLoading ? 0.6 : 1 }}
            onClick={handleFollow}
            disabled={followLoading}
          >
            {isFollowing ? '✓ Following' : '+ Follow'}
          </button>
          <button
            style={st.messageBtn}
            onClick={() => navigate(`/chat?dm=${userId}`)}
          >
            💬 Message
          </button>
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div style={st.statsStrip}>
        {[
          { n: totalRounds, l: 'Rounds' },
          { n: totalCourses, l: 'Courses' },
          { n: bestScore ?? '—', l: 'Best Score' },
        ].map(({ n, l }, i, arr) => (
          <div key={l} style={{ ...st.statCell, borderRight: i < arr.length - 1 ? '1px solid #f0f4f1' : 'none' }}>
            <div style={st.statNum}>{n}</div>
            <div style={st.statLbl}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── PRIVATE GATE ── */}
      {!canSee ? (
        <div style={st.privateGate}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={st.privateTitle}>This account is private</div>
          <div style={st.privateSub}>Follow {profile?.username || 'this player'} to see their rounds and stats.</div>
          <button
            style={{ ...st.followBtn, ...st.notFollowingBtn, marginTop: 20 }}
            onClick={handleFollow}
            disabled={followLoading}
          >
            {followLoading ? '...' : '+ Follow'}
          </button>
        </div>
      ) : (
        <div style={st.body}>

          {/* ── PHOTOS (top) ── */}
          {photoRounds.length > 0 && (
            <div style={st.section}>
              <div style={st.sectionTitle}>
                Photos <span style={st.sectionCount}>{photoRounds.length}</span>
              </div>
              <div style={st.photoGrid}>
                {photoRounds.map(r => (
                  <div key={r.id} style={st.photoCell} onClick={() => setLightboxUrl(r.photo_url)}>
                    <img src={r.photo_url} alt="" style={st.photoImg} />
                    {r.course_name && (
                      <div style={st.photoOverlay}>
                        <div style={st.photoCourseName}>{r.course_name}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── SEASON IN REVIEW ── */}
          {roundsThisYear.length > 0 && (
            <div style={st.section}>
              <div style={st.sectionTitle}>⛳ {thisYear} Season</div>
              <div style={st.seasonGrid}>
                <div style={st.seasonCard}>
                  <div style={st.seasonNum}>{roundsThisYear.length}</div>
                  <div style={st.seasonLbl}>Rounds</div>
                </div>
                <div style={st.seasonCard}>
                  <div style={st.seasonNum}>{coursesThisYear}</div>
                  <div style={st.seasonLbl}>Courses</div>
                </div>
                <div style={st.seasonCard}>
                  <div style={st.seasonNum}>{avgThisYear ?? '—'}</div>
                  <div style={st.seasonLbl}>Avg Score</div>
                </div>
              </div>
            </div>
          )}

          {/* ── ACHIEVEMENTS ── */}
          {achievements.length > 0 && (
            <div style={st.section}>
              <div style={st.sectionTitle}>
                Achievements <span style={st.sectionCount}>{achievements.length} earned</span>
              </div>
              <div style={st.achieveGrid}>
                {achievements.map(a => (
                  <div key={a.name} style={st.achieveCard}>
                    <div style={st.achieveIcon}>{a.icon}</div>
                    <div style={st.achieveName}>{a.name}</div>
                    <div style={st.achieveDesc}>{a.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── RECENT ACTIVITY ── */}
          {rounds.length > 0 ? (
            <div style={st.section}>
              <div style={st.sectionTitle}>
                Recent Activity <span style={st.sectionCount}>{rounds.length} rounds</span>
              </div>
              {recentRounds.map(round => {
                const bg = scoreBadgeColor(round.score, round.par)
                return (
                  <div key={round.id} style={st.activityCard}>
                    <div style={st.activityTop}>
                      <div style={st.activityLeft}>
                        <div style={st.activityCourse}>{round.course_name || 'Round'}</div>
                        <div style={st.activityDate}>{formatDate(round.created_at)}</div>
                      </div>
                      {round.score != null && (
                        <div style={{ ...st.activityScore, background: bg }}>
                          <div style={st.activityScoreNum}>{round.score}</div>
                          {round.par && (
                            <div style={st.activityScoreDiff}>
                              {round.score - round.par === 0 ? 'E' : round.score - round.par > 0 ? `+${round.score - round.par}` : `${round.score - round.par}`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {round.caption && <div style={st.activityCaption}>{round.caption}</div>}
                    {round.photo_url && (
                      <img src={round.photo_url} alt="" style={st.activityPhoto} onClick={() => setLightboxUrl(round.photo_url)} />
                    )}
                    {(round.par || round.putts != null) && (
                      <div style={st.activityStats}>
                        {round.par && <span style={st.activityStat}>Par {round.par}</span>}
                        {round.fir != null && <span style={st.activityStat}>FIR {round.fir}</span>}
                        {round.gir != null && <span style={st.activityStat}>GIR {round.gir}</span>}
                        {round.putts != null && <span style={st.activityStat}>{round.putts} putts</span>}
                      </div>
                    )}
                  </div>
                )
              })}
              {olderRounds.length > 0 && (
                <>
                  {showAllRounds && olderRounds.map(round => (
                    <div key={round.id} style={st.compactRoundRow}>
                      <div style={st.compactRoundInfo}>
                        <div style={st.compactCourse}>{round.course_name || 'Round'}</div>
                        <div style={st.compactDate}>{formatDateShort(round.created_at)}</div>
                      </div>
                      {round.score != null && (
                        <div style={{ ...st.compactScore, background: scoreBadgeColor(round.score, round.par) }}>
                          <div style={st.compactScoreNum}>{scoreDiffLabel(round.score, round.par)}</div>
                        </div>
                      )}
                    </div>
                  ))}
                  <button style={st.showMoreBtn} onClick={() => setShowAllRounds(p => !p)}>
                    {showAllRounds ? '↑ Show less' : `↓ Show ${olderRounds.length} more`}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div style={st.section}>
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7c6e', fontSize: 13 }}>
                No rounds logged yet.
              </div>
            </div>
          )}

          {/* ── COURSE PASSPORT ── */}
          {courses.length > 0 && (
            <div style={st.section}>
              <div style={st.sectionTitle}>
                Course Passport <span style={st.sectionCount}>{totalCourses} courses</span>
              </div>
              <div style={st.courseGrid}>
                {courses.map(c => (
                  <Link key={c.name} to={`/course?name=${encodeURIComponent(c.name)}`} style={{ textDecoration: 'none' }}>
                    <div style={st.courseCard}>
                      <div style={st.stampBadge}>{c.timesPlayed}x</div>
                      <div style={st.courseName}>{c.name}</div>
                      {c.bestScore != null && (
                        <div style={{ ...st.courseScoreBadge, background: scoreBadgeColor(c.bestScore, c.par) }}>
                          {scoreDiffLabel(c.bestScore, c.par)}
                        </div>
                      )}
                      <div style={st.courseLastPlayed}>Last: {formatDateShort(c.lastPlayed)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const st = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },
  loadingScreen: {
    minHeight: '100vh', background: '#0e1f13',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
  },
  backBtn: {
    marginTop: 20, background: 'none', border: '1px solid rgba(255,255,255,0.2)',
    color: 'white', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13
  },

  // ── Topbar + nav ─────────────────────────────────────
  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  backBtnTop: {
    background: 'none', border: 'none', color: '#c8f135',
    fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, minWidth: 60,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135', letterSpacing: 2
  },

  // ── Hero ─────────────────────────────────────────────
  hero: { background: '#0e1f13', overflow: 'hidden' },
  heroContent: { display: 'flex', gap: 18, padding: '24px 20px 16px' },
  heroAvatar: {
    width: 88, height: 88, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #2aad56, #1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 46, border: '3px solid #0e1f13',
    boxShadow: '0 0 0 3px rgba(200,241,53,0.4)', overflow: 'hidden'
  },
  heroInfo: { flex: 1, paddingTop: 8 },
  heroName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: 'white', letterSpacing: 0.5, lineHeight: 1.1, marginBottom: 3
  },
  heroHandle: { fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 6 },
  heroBio: { fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.45, marginBottom: 10 },
  heroFollowRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  heroFollowBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'baseline', gap: 4
  },
  heroFollowNum: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 15, color: 'white'
  },
  heroFollowLbl: { fontSize: 11, color: 'rgba(255,255,255,0.45)' },
  heroFollowDot: { fontSize: 11, color: 'rgba(255,255,255,0.25)' },
  heroMeta: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  heroMetaItem: { fontSize: 11, color: 'rgba(255,255,255,0.4)' },
  hcpBadge: {
    display: 'inline-block', background: 'rgba(200,241,53,0.15)',
    border: '1px solid rgba(200,241,53,0.3)', color: '#c8f135',
    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, letterSpacing: 0.5
  },

  // ── Follow button ─────────────────────────────────────
  followRow: { padding: '0 20px 20px', display: 'flex', gap: 10, flexWrap: 'wrap' },
  followBtn: {
    borderRadius: 22, padding: '9px 28px', fontSize: 14,
    fontWeight: 800, cursor: 'pointer', border: 'none',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  notFollowingBtn: { background: '#c8f135', color: '#0e1f13' },
  messageBtn: {
    borderRadius: 22, padding: '9px 20px', fontSize: 14,
    fontWeight: 800, cursor: 'pointer',
    border: '1.5px solid rgba(255,255,255,0.3)',
    background: 'transparent', color: 'white',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  followingBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.25)',
    color: 'rgba(255,255,255,0.8)'
  },

  // ── Stats strip ───────────────────────────────────────
  statsStrip: {
    display: 'flex', background: 'white',
    borderBottom: '1px solid #f0f4f1', position: 'relative', zIndex: 1
  },
  statCell: { flex: 1, textAlign: 'center', padding: '16px 8px' },
  statNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#0e1f13', lineHeight: 1
  },
  statLbl: { fontSize: 9, color: '#9aaa9d', textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 },

  // ── Private gate ──────────────────────────────────────
  privateGate: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '64px 24px', textAlign: 'center'
  },
  privateTitle: { fontWeight: 700, fontSize: 16, color: '#0e1f13', marginBottom: 8 },
  privateSub: { fontSize: 13, color: '#6b7c6e', lineHeight: 1.5 },

  // ── Body ──────────────────────────────────────────────
  body: { maxWidth: 640, margin: '0 auto', padding: '16px 16px 60px' },
  section: {
    background: 'white', borderRadius: 18,
    padding: '20px 18px', marginBottom: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  sectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13',
    letterSpacing: 1, textTransform: 'uppercase',
    marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
  },
  sectionCount: {
    fontFamily: "'Barlow', sans-serif", fontWeight: 600, fontSize: 11,
    color: '#6b7c6e', textTransform: 'none', letterSpacing: 0
  },

  // ── Photos ────────────────────────────────────────────
  photoGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 4, borderRadius: 12, overflow: 'hidden'
  },
  photoCell: {
    position: 'relative', aspectRatio: '1',
    overflow: 'hidden', background: '#e2ebe4', cursor: 'pointer'
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  photoOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
    padding: '16px 6px 6px'
  },
  photoCourseName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700, fontSize: 10, color: 'white', lineHeight: 1.2, letterSpacing: 0.3
  },

  // ── Season ────────────────────────────────────────────
  seasonGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  seasonCard: { background: '#f4f7f5', borderRadius: 12, padding: '14px 10px', textAlign: 'center' },
  seasonNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: '#0e1f13', lineHeight: 1
  },
  seasonLbl: { fontSize: 10, color: '#6b7c6e', marginTop: 5, textTransform: 'uppercase', letterSpacing: 0.8 },

  // ── Achievements ─────────────────────────────────────
  achieveGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  achieveCard: {
    background: '#f4f7f5', borderRadius: 12,
    padding: '14px 10px', textAlign: 'center', border: '1px solid #e2ebe4'
  },
  achieveIcon: { fontSize: 26, marginBottom: 6 },
  achieveName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 12, color: '#0e1f13',
    letterSpacing: 0.3, marginBottom: 3, lineHeight: 1.2
  },
  achieveDesc: { fontSize: 9, color: '#6b7c6e', lineHeight: 1.3 },

  // ── Activity cards ────────────────────────────────────
  activityCard: {
    background: '#f4f7f5', borderRadius: 14,
    padding: '14px', marginBottom: 10, border: '1px solid #e2ebe4'
  },
  activityTop: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 10, marginBottom: 8
  },
  activityLeft: { flex: 1 },
  activityCourse: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: '#0e1f13', lineHeight: 1.1
  },
  activityDate: { fontSize: 11, color: '#6b7c6e', marginTop: 3 },
  activityScore: { borderRadius: 10, padding: '6px 12px', textAlign: 'center', flexShrink: 0 },
  activityScoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: 'white', lineHeight: 1
  },
  activityScoreDiff: { fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 600, marginTop: 1 },
  activityCaption: { fontSize: 13, color: '#4a5e4f', lineHeight: 1.45, marginBottom: 10, fontStyle: 'italic' },
  activityPhoto: {
    width: '100%', borderRadius: 10, display: 'block',
    marginBottom: 10, cursor: 'pointer', maxHeight: 280, objectFit: 'cover'
  },
  activityStats: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  activityStat: {
    background: 'white', borderRadius: 20,
    padding: '4px 10px', fontSize: 11, fontWeight: 600,
    color: '#4a5e4f', border: '1px solid #e2ebe4'
  },

  // ── Compact older rounds ──────────────────────────────
  compactRoundRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 0', borderBottom: '1px solid #f0f4f1'
  },
  compactRoundInfo: { flex: 1 },
  compactCourse: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700, fontSize: 15, color: '#0e1f13'
  },
  compactDate: { fontSize: 11, color: '#6b7c6e', marginTop: 1 },
  compactScore: { borderRadius: 10, padding: '4px 12px', textAlign: 'center', minWidth: 52 },
  compactScoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 14, color: 'white', lineHeight: 1.1
  },
  showMoreBtn: {
    width: '100%', background: 'none', border: 'none',
    color: '#1a7a3c', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', padding: '12px 0 0',
    fontFamily: "'Barlow', sans-serif"
  },

  // ── Course Passport ───────────────────────────────────
  courseGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 },
  courseCard: {
    background: '#f4f7f5', borderRadius: 14,
    padding: '14px 12px', position: 'relative', border: '1.5px solid #e2ebe4'
  },
  stampBadge: {
    position: 'absolute', top: 10, right: 10,
    background: '#0e1f13', color: '#c8f135',
    borderRadius: 20, padding: '2px 8px',
    fontSize: 10, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  courseName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13',
    lineHeight: 1.2, marginBottom: 10, paddingRight: 32
  },
  courseScoreBadge: {
    display: 'inline-block', borderRadius: 8,
    padding: '3px 10px', fontSize: 11, fontWeight: 700,
    color: 'white', marginBottom: 8,
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  courseLastPlayed: { fontSize: 10, color: '#6b7c6e' },

  // ── Lightbox ─────────────────────────────────────────
  lightboxOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  lightboxImg: { maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 },
  lightboxClose: {
    position: 'absolute', top: 16, right: 16,
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: 'white', borderRadius: '50%', width: 36, height: 36,
    fontSize: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  // ── Follow modal ─────────────────────────────────────
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  modalSheet: {
    background: 'white', borderRadius: 20,
    width: '100%', maxWidth: 480, maxHeight: '70vh', overflowY: 'auto', paddingBottom: 24
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px 14px', borderBottom: '1px solid #f0f4f1',
    position: 'sticky', top: 0, background: 'white', zIndex: 1
  },
  modalTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 16, color: '#0e1f13', letterSpacing: 0.5
  },
  modalClose: {
    background: '#f4f7f5', border: 'none', borderRadius: '50%',
    width: 28, height: 28, fontSize: 12, cursor: 'pointer', color: '#6b7c6e',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  modalEmpty: { padding: '24px', textAlign: 'center', color: '#6b7c6e', fontSize: 13 },
  modalRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', borderBottom: '1px solid #f0f4f1'
  },
  modalAvatar: {
    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #2aad56, #1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, overflow: 'hidden'
  },
  modalUserInfo: { flex: 1 },
  modalUserName: { fontWeight: 700, fontSize: 14, color: '#0e1f13' },
  modalUserHandle: { fontSize: 12, color: '#6b7c6e', marginTop: 1 }
}
